import { asky, baseApi, rtkq } from '@app/base';
import type { FilterRule } from '@webinex/asky';
import { PatientPortalAppointmentRequest } from './appointmentRequestsTypes';
import {
  GetPatientPortalAppointmentRequestDocument,
  GetPatientPortalAppointmentRequestsDocument,
} from './graphql';

function patientPortalAppointmentRequestVisitorIdsFilter(visitorIds?: string[]): FilterRule | false {
  const values = visitorIds?.map((x) => x.trim()).filter(Boolean) ?? [];

  if (values.length === 0) {
    return false;
  }

  const children = values.map(
    (visitorId): FilterRule => ({
      fieldId: 'visitorId',
      operator: 'contains',
      value: visitorId,
    }),
  );

  return children.length === 1 ? children[0] : { operator: 'or', children };
}

export const appointmentRequestsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getPatientPortalAppointmentRequests: build.query<
      PatientPortalAppointmentRequest[],
      {
        statuses?: string[];
        visitorIds?: string[];
        from?: string;
        to?: string;
      }
    >({
      queryFn: rtkq.graphql(() => GetPatientPortalAppointmentRequestsDocument, {
        select: (x) => x.appointmentRequests as PatientPortalAppointmentRequest[],
        vars: ({ statuses, visitorIds, from, to }) => ({
          query: {
            filterRule: asky.filter.create([
              statuses?.length ? { fieldId: 'status', operator: 'in', values: statuses } : false,
              patientPortalAppointmentRequestVisitorIdsFilter(visitorIds),
              from ? { fieldId: 'requestedPeriodStart', operator: '>=', value: from } : false,
              to ? { fieldId: 'requestedPeriodStart', operator: '<=', value: to } : false,
            ]),
          },
        }),
      }),
      providesTags: ['schedule:appointment-request'],
    }),

    getPatientPortalAppointmentRequest: build.query<PatientPortalAppointmentRequest, { id: string }>({
      queryFn: rtkq.graphql(() => GetPatientPortalAppointmentRequestDocument, {
        select: (x) => x.appointmentRequest as PatientPortalAppointmentRequest,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: ['schedule:appointment-request'],
    }),
  }),
});

export const { useGetPatientPortalAppointmentRequestQuery, useGetPatientPortalAppointmentRequestsQuery } =
  appointmentRequestsApi;
